import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useRevitData } from '../../../shared/providers/RevitDataContext';
import { useQuickFillStore } from '../../../shared/stores/useQuickFillStore';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import {
  useFamiliesWithParameters,
  useSelectedCategories,
} from '../../../shared/stores/useParameterWorkflowStore';
import {
  normalizeRevitParameterName,
  sanitizeRevitParameterNames,
} from '../../../shared/lib/revitParameterNames';
import { perf } from '../../../shared/lib/perfLogger';
import {
  collectMappedQuickFillColumns,
  synchronizeQuickFillConfiguredParameters,
} from './useQuickFillConfigSync';
import {
  getLastQuickFillDataCacheProbeDetails,
  hasQuickFillDataCache,
  useQuickFillData,
  type QuickFillFetchResult,
} from './useQuickFillData';
import type { QuickFillSnapshotShapeOption } from '../../../shared/api/grpc/quickFillClient';
import {
  appendRequestedParameterIntentCacheIdentity,
  filterRequestedParameterIntents,
  selectAuthoritativeCacheScope,
  type RequestedParameterIntentInput,
} from '../../../shared/api/grpc/requestedParameterIntent';
import type { ValidationQuickFillFocus } from '../../../shared/types/validationNavigation.types';
import {
  QUICK_FILL_LOAD_TOTAL_STEPS,
  type QuickFillLoadProgress,
} from '../lib/quickFillLoadProgress';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import { useQuickFillPayloadSelection } from './useQuickFillPayloadSelection';
import { useQuickFillRefreshCoordinator } from './useQuickFillRefreshCoordinator';
import { buildQuickFillCatalogCacheScopeKey } from '../lib/quickFillCatalogMode';

import {
  sampleNames,
  buildRequestedColumns,
  buildFetchMetrics,
  buildCacheReuseProgress,
  buildQuickFillDataScopeSignature,
  buildQuickFillRefreshKey,
  buildQuickFillDatasetSourceKey,
  buildQuickFillDataScope,
  createHostProgressGate,
  mapHostQuickFillLoadProgress,
} from '../lib/quickFillInitializationSummary';

// Quiet window before a cache-scope change triggers a snapshot reload; Revit
// republishes ModelChanged per transaction during writes, so anything shorter
// turns one apply into a fetch storm.
const DEFER_UNPLACED_FAMILY_CATALOG = true;

export function useQuickFillInitialization(
  validationScope?: ValidationQuickFillFocus | null,
  snapshotShape: QuickFillSnapshotShapeOption = 'familyType',
  baseCacheScopeKey?: string | null,
  mutationActive = false,
  requestedParameterIntents: readonly RequestedParameterIntentInput[] = []
) {
  // Primary: single gRPC Quick Fill snapshot call
  const {
    families: grpcFamilies,
    pipeElements: grpcPipeElements,
    availableParameters: grpcAvailableParams,
    isLoaded: isGrpcLoaded,
    error: grpcError,
    fetchQuickFillData,
    cacheScopeKey: authoritativeResponseCacheScopeKey,
  } = useQuickFillData();
  const notifiedCacheScopeKey = appendRequestedParameterIntentCacheIdentity(
    buildQuickFillCatalogCacheScopeKey(baseCacheScopeKey, DEFER_UNPLACED_FAMILY_CATALOG),
    requestedParameterIntents
  );
  const cacheScopeKey = selectAuthoritativeCacheScope(
    notifiedCacheScopeKey,
    authoritativeResponseCacheScopeKey
  );

  // Fallback: RevitDataContext (loaded globally by AppContent)
  const {
    elements: fallbackElements,
    families: fallbackFamilies,
    isLoaded: isFallbackLoaded,
    refreshData: refreshRevitData,
  } = useRevitData();

  const properties = useQuickFillStore(state => state.properties);
  const extraProperties = useQuickFillStore(state => state.extraProperties);
  const initializeProperties = useQuickFillStore(state => state.initializeProperties);
  const mappingConfiguration = useConfigurationStore((state) => state.mappingConfiguration);
  const workflowFamiliesWithParameters = useFamiliesWithParameters();
  const workflowSelectedCategories = useSelectedCategories();
  const quickFillDataScope = useMemo(
    () => buildQuickFillDataScope(
      workflowFamiliesWithParameters,
      workflowSelectedCategories,
      validationScope
    ),
    [workflowFamiliesWithParameters, workflowSelectedCategories, validationScope]
  );

  const [isInitializing, setIsInitializing] = useState(false);
  const [loadProgress, setLoadProgress] = useState<QuickFillLoadProgress>({
    phase: 'preparing',
    message: 'Preparing Quick Fill data',
    detail: 'Reading selected mappings before Revit is queried',
    currentStep: 1,
    totalSteps: QUICK_FILL_LOAD_TOTAL_STEPS,
  });
  const initializationDoneRef = useRef(false);
  const initializationStartedRef = useRef(false);
  const lastRefreshKeyRef = useRef<string>('');
  const [quickFillRefreshKey, setQuickFillRefreshKey] = useState('');

  const {
    allowE2eQuickFillFallback,
    preferGrpcPayload,
    preferGrpcPayloadOnly,
    dataSourceSettled,
    scopedPipeElements,
    scopedPipeFamilies,
    validationScopeResolvedEmpty,
    availableParameters,
    sharedParametersOptions,
  } = useQuickFillPayloadSelection({
    grpcFamilies,
    grpcPipeElements,
    grpcAvailableParams,
    isGrpcLoaded,
    grpcError,
    fallbackElements,
    fallbackFamilies,
    isFallbackLoaded,
    validationScope,
  });

  const runQuickFillFetch = useCallback(async (
    params: string[] | undefined,
    options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
  ): Promise<QuickFillFetchResult> => {
    if (preferGrpcPayload) {
      const quickFillResult = await fetchQuickFillData(params, {
        ...options,
        snapshotShape,
        cacheScopeKey,
        dataScope: quickFillDataScope,
        deferUnplacedFamilyCatalog: DEFER_UNPLACED_FAMILY_CATALOG,
        requestedParameters: filterRequestedParameterIntents(
          requestedParameterIntents,
          params ?? []
        ),
      });
      if (!quickFillResult.success && !preferGrpcPayloadOnly) {
        perf.warn('QF-Init', 'gRPC Quick Fill data unavailable, falling back to RevitData refresh', {
          success: quickFillResult.success,
          source: quickFillResult.source,
          familiesCount: quickFillResult.familiesCount,
          elementsCount: quickFillResult.elementsCount,
          availableParametersCount: quickFillResult.availableParametersCount,
        });
        await refreshRevitData(params);
        return {
          success: true,
          source: 'fallback',
          familiesCount: fallbackFamilies.length,
          elementsCount: fallbackElements.length,
          availableParametersCount: 0,
        };
      } else if (!quickFillResult.success) {
        perf.warn('QF-Init', 'gRPC Quick Fill data unavailable in gRPC-only runtime; skipping legacy fallback refresh', {
          success: quickFillResult.success,
          source: quickFillResult.source,
        });
      } else if (
        quickFillResult.familiesCount === 0 &&
        quickFillResult.elementsCount === 0 &&
        quickFillResult.availableParametersCount === 0
      ) {
        if (allowE2eQuickFillFallback) {
          perf.warn('QF-Init', 'gRPC Quick Fill returned an empty e2e payload, falling back to mock RevitDataContext refresh', {
            success: quickFillResult.success,
            source: quickFillResult.source,
            snapshotShape,
          });
          await refreshRevitData(params);
          return {
            success: true,
            source: 'fallback',
            familiesCount: fallbackFamilies.length,
            elementsCount: fallbackElements.length,
            availableParametersCount: 0,
          };
        }

        perf.warn('QF-Init', 'gRPC Quick Fill returned an empty payload; skipping legacy fallback refresh', {
          success: quickFillResult.success,
          source: quickFillResult.source,
          snapshotShape,
          familiesCount: quickFillResult.familiesCount,
          elementsCount: quickFillResult.elementsCount,
          availableParametersCount: quickFillResult.availableParametersCount,
        });
      }
      return quickFillResult;
    }

    await refreshRevitData(params);
    return {
      success: true,
      source: 'fallback',
      familiesCount: fallbackFamilies.length,
      elementsCount: fallbackElements.length,
    availableParametersCount: 0,
  };
  }, [
    fallbackElements.length,
    fallbackFamilies.length,
    fetchQuickFillData,
    cacheScopeKey,
    allowE2eQuickFillFallback,
    preferGrpcPayload,
    preferGrpcPayloadOnly,
    refreshRevitData,
    snapshotShape,
    quickFillDataScope,
    requestedParameterIntents,
  ]);

  const buildCurrentQuickFillRequest = useCallback(() => {
    const currentProps = useQuickFillStore.getState().properties || [];
    const currentExtras = useQuickFillStore.getState().extraProperties || [];
    const mappedColumns = collectMappedQuickFillColumns(mappingConfiguration, currentProps, currentExtras);
    const requestedProperties = [...currentProps, ...currentExtras, ...mappedColumns];
    const parameterNames = sanitizeRevitParameterNames(
      requestedProperties
        .filter(p => p?.checked)
        .map(p => p?.revitParameter)
    ).map(normalizeRevitParameterName);

    return {
      parameterNames,
      requestedColumns: buildRequestedColumns(requestedProperties),
      requestedParameters: filterRequestedParameterIntents(
        requestedParameterIntents,
        parameterNames
      ),
    };
  }, [mappingConfiguration, requestedParameterIntents]);

  // Initialize: try gRPC, data will fallback automatically via revitFamilies memo
  useEffect(() => {
    if (mutationActive) {
      return;
    }

    const initializeQuickFill = async () => {
      if (initializationDoneRef.current || initializationStartedRef.current) {
        return;
      }
      initializationStartedRef.current = true;
      const t0 = performance.now();
      perf.log('QF-Init', '=== QuickFill initialization START ===');
      try {
        const tInitializeProperties = performance.now();
        initializeProperties();
        const liveQuickFillState = useQuickFillStore.getState();
        const liveConfigurationState = useConfigurationStore.getState?.();
        synchronizeQuickFillConfiguredParameters(
          liveQuickFillState.properties || [],
          liveConfigurationState?.menuConfiguration,
          liveConfigurationState?.connectorBindingConfiguration,
          liveQuickFillState.updatePropertyRevitParameter
        );
        perf.slow('QF-Init', 'initialize properties', tInitializeProperties, 25);

        const tRequest = performance.now();
        const { parameterNames, requestedColumns } = buildCurrentQuickFillRequest();
        perf.slow('QF-Init', 'build current request tuple', tRequest, 25, {
          parameterCount: parameterNames.length,
          requestedColumnCount: requestedColumns.length,
          snapshotShape,
          cacheScopeKey,
          dataScope: buildQuickFillDataScopeSignature(quickFillDataScope),
        });

        const initialRefreshKey = buildQuickFillRefreshKey(cacheScopeKey, snapshotShape, parameterNames, quickFillDataScope);
        lastRefreshKeyRef.current = initialRefreshKey;
        setQuickFillRefreshKey(initialRefreshKey);

        const params = parameterNames.length > 0 ? parameterNames : undefined;
        const tCacheProbe = performance.now();
        const cachedQuickFillResponse = hasQuickFillDataCache(params, {
          snapshotShape,
          cacheScopeKey,
          dataScope: quickFillDataScope,
        });
        const cacheProbeDetails = getLastQuickFillDataCacheProbeDetails();
        perf.slow('QF-Init', 'response cache probe', tCacheProbe, cachedQuickFillResponse ? 80 : 10, {
          cacheHit: cachedQuickFillResponse,
          memoryHit: cacheProbeDetails?.memoryHit ?? false,
          persistentMarkerHit: cacheProbeDetails?.persistentMarkerHit ?? false,
          persistentMarkerCount: cacheProbeDetails?.persistentMarkerCount ?? 0,
          sessionHit: cacheProbeDetails?.sessionHit ?? false,
          parameterCount: parameterNames.length,
          snapshotShape,
          cacheScopeKey,
          dataScope: buildQuickFillDataScopeSignature(quickFillDataScope),
        });
        const cacheAvailable = preferGrpcPayload && cachedQuickFillResponse;

        if (cacheAvailable) {
          setIsInitializing(false);
          setLoadProgress(buildCacheReuseProgress({
            phase: 'processing',
            currentStep: 3,
            detail: parameterNames.length > 0
              ? `${parameterNames.length} requested Revit parameter${parameterNames.length === 1 ? '' : 's'} already cached`
              : snapshotShape === 'instanceOnly'
                ? 'Instance row metadata is already cached'
                : 'Family/type metadata is already cached',
            parameterCount: parameterNames.length,
            requestedColumnCount: requestedColumns.length,
          }));
        } else {
          setIsInitializing(true);
          setLoadProgress({
            phase: 'requesting',
            message: preferGrpcPayload ? 'Requesting Quick Fill snapshot from Revit' : 'Requesting Quick Fill data from the Revit bridge',
            detail: parameterNames.length > 0
              ? `${parameterNames.length} requested Revit parameter${parameterNames.length === 1 ? '' : 's'}`
              : snapshotShape === 'instanceOnly'
                ? 'No checked Revit parameters yet; loading instance row metadata'
                : 'No checked Revit parameters yet; loading family/type metadata',
            currentStep: 2,
            totalSteps: QUICK_FILL_LOAD_TOTAL_STEPS,
            metrics: buildFetchMetrics(null, parameterNames.length, requestedColumns.length),
          });
        }

        perf.log('QF-Init', 'Quick Fill fetch START', {
          paramCount: parameterNames.length,
          sampleRequestedParams: sampleNames(parameterNames),
          sampleRequestedColumns: sampleNames(requestedColumns),
          cacheScopeKey,
          snapshotShape,
          dataScope: buildQuickFillDataScopeSignature(quickFillDataScope),
          strategy: preferGrpcPayload ? 'grpc-primary' : 'revit-fallback-only',
        });
        const tFetch = performance.now();
        const shouldPublishHostProgress = createHostProgressGate();
        const fetchResult = await runQuickFillFetch(params, {
          onProgress: (progress) => {
            if (!shouldPublishHostProgress(progress)) {
              return;
            }
            const tProgress = performance.now();
            setLoadProgress(mapHostQuickFillLoadProgress(
              progress,
              parameterNames.length,
              requestedColumns.length
            ));
            perf.slow('QF-Init', 'progress message handling', tProgress, 25, {
              phase: progress.phase,
              processed: progress.processed,
              total: progress.total,
              percent: progress.percent,
              cacheStatus: progress.cacheStatus,
              resultReady: progress.resultReady,
            });
          },
        });
        const authoritativeRefreshKey = buildQuickFillRefreshKey(
          fetchResult.cacheScopeKey ?? cacheScopeKey,
          snapshotShape,
          parameterNames,
          quickFillDataScope
        );
        lastRefreshKeyRef.current = authoritativeRefreshKey;
        setQuickFillRefreshKey(authoritativeRefreshKey);
        perf.slow('QF-Init', 'runQuickFillFetch resolved', tFetch, cacheAvailable ? 120 : 100, {
          source: fetchResult.source,
          success: fetchResult.success,
          families: fetchResult.familiesCount,
          elements: fetchResult.elementsCount,
          availableParameters: fetchResult.availableParametersCount,
          cacheExpected: cacheAvailable,
        });
        if (fetchResult.source === 'cache') {
          setLoadProgress(buildCacheReuseProgress({
            detail: `${fetchResult.familiesCount} families, ${fetchResult.elementsCount} elements, ${fetchResult.availableParametersCount} available parameters`,
            parameterCount: parameterNames.length,
            requestedColumnCount: requestedColumns.length,
            result: fetchResult,
          }));
        } else {
          setLoadProgress({
            phase: 'processing',
            message: fetchResult.success
              ? 'Processing Revit Quick Fill snapshot'
              : 'Revit Quick Fill data was not returned',
            detail: fetchResult.success
              ? `${fetchResult.familiesCount} families, ${fetchResult.elementsCount} elements, ${fetchResult.availableParametersCount} available parameters`
              : 'The page is waiting for a usable Revit payload',
            currentStep: 3,
            totalSteps: QUICK_FILL_LOAD_TOTAL_STEPS,
            metrics: buildFetchMetrics(fetchResult, parameterNames.length, requestedColumns.length),
          });
        }
        perf.measure('QF-Init', 'data fetch END', t0);
      } catch (_error) {
        // Error handled by useQuickFillData
        setLoadProgress({
          phase: 'error',
          message: 'Quick Fill data load failed',
          detail: 'Check the Revit connector logs for the failed request',
          currentStep: 2,
          totalSteps: QUICK_FILL_LOAD_TOTAL_STEPS,
        });
      } finally {
        initializationStartedRef.current = false;
        setIsInitializing(false);
        initializationDoneRef.current = true;
        perf.measure('QF-Init', '=== QuickFill initialization DONE ===', t0);
      }
    };
    initializeQuickFill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeProperties, buildCurrentQuickFillRequest, runQuickFillFetch, snapshotShape, cacheScopeKey, quickFillDataScope, mutationActive]);

  // Re-fetch when checked columns change. Model writes republish ModelChanged
  // per transaction, so an apply bumps the cache scope dozens of times in a few
  // seconds — without a quiet window each bump fired a full snapshot reload
  // (54 getQuickFillData calls in 15s). Debounce to one reload per burst.
  useQuickFillRefreshCoordinator({
    mutationActive,
    initializationDoneRef,
    lastRefreshKeyRef,
    isInitializing,
    preferGrpcPayload,
    snapshotShape,
    cacheScopeKey,
    dataScope: quickFillDataScope,
    propertiesSignal: properties,
    extraPropertiesSignal: extraProperties,
    buildRequest: buildCurrentQuickFillRequest,
    runFetch: runQuickFillFetch,
    setLoadProgress,
    setRefreshKey: setQuickFillRefreshKey,
  });

  const quickFillDatasetSourceKey = useMemo(
    () => buildQuickFillDatasetSourceKey(quickFillRefreshKey, validationScope),
    [quickFillRefreshKey, validationScope]
  );

  return {
    isInitializing,
    loadProgress,
    initializationDoneRef,
    pipeFamilies: scopedPipeFamilies,
    pipeElements: scopedPipeElements,
    availableParameters,
    sharedParametersOptions,
    isRevitDataLoaded: dataSourceSettled,
    validationScopeResolvedEmpty,
    quickFillDatasetSourceKey,
    refreshData: fetchQuickFillData,
  };
}
